import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useLocation, useParams } from 'react-router-dom';
import { Button, Col, DatePicker, Form, Input, Row, Switch, Typography } from 'antd';
import Page from '../page';
import useToastContext from '../../hooks/useToastContext';
import {
  SwitchContainer,
  WhiteDisabledInput,
} from '../../pages/issuer/card-product';
import useGetCardAffinityGroup from '../../hooks/CardAffinityGroup/useGetCardAffinityGroup';
import useUpdateCardAffinityGroup from '../../hooks/update/useUpdateCardAffinityGroup';
import Emboss, { EmbossFormInputs } from '../Emboss/emboss';
import Fees from '../Fees/fees';
import { FeesFormInputs } from '../Fees/modal-fees';
import Charges from '../Charges/charges';
import { ChargesFormInputs } from '../Charges/modal-charges';
import Limits, { LimitsFormInputs } from '../Limits/limits';
import UsageLimit, { UsageLimits } from '../UsageLimits/usage-limits';

type CardAffinityGroupFormInputs = {
  id: number;
  name: string;
  startDate: Dayjs;
  endDate: Dayjs;
  active: boolean;
};

const AffinityGroup = ({ cardAffinityGroup, id }) => {
  const toast = useToastContext();
  const { cardAffinityGroupId } = useParams<{ cardAffinityGroupId: string }>();
  const [form] = Form.useForm();
  const { loading, error, executePatch } = useUpdateCardAffinityGroup(cardAffinityGroupId);
  const [active, setActive] = useState(false);
  const [submittable, setSubmittable] = useState(false);

  useEffect(() => {
    form.setFieldValue('id', cardAffinityGroup?.id);
    form.setFieldValue('name', cardAffinityGroup?.name);
    form.setFieldValue('startDate', cardAffinityGroup?.start_date && dayjs(cardAffinityGroup?.start_date));
    form.setFieldValue('endDate', cardAffinityGroup?.end_date && dayjs(cardAffinityGroup?.end_date));
    setActive(cardAffinityGroup?.active);
  }, [cardAffinityGroup]);

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  const onSubmit = async (data: CardAffinityGroupFormInputs) => {
    const jsonPatch = [];
    data?.name &&
      jsonPatch.push({
        op: 'replace',
        path: '/name',
        value: data?.name,
      });
    jsonPatch.push({
      op: 'replace',
      path: '/active',
      value: active,
    });
    data?.startDate &&
      jsonPatch.push({
        op: 'replace',
        path: '/start_date',
        value: data?.startDate?.format("YYYY-MM-DD"),
      });
    data?.endDate &&
      jsonPatch.push({
        op: 'replace',
        path: '/end_date',
        value: data?.endDate?.format("YYYY-MM-DD"),
      });

    try {
      const response = await executePatch({ data: jsonPatch });

      if (response.status === 200) {
        toast.success('Exito', 'Grupo de afinidad actualizado correctamente!.');
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error: ${error}, porfavor, intenta nuevamente.`);
    }
  };

  return (
    <Form<CardAffinityGroupFormInputs>
      name="basic"
      layout="vertical"
      autoComplete="off"
      form={form}
      onFinish={onSubmit}
      onFinishFailed={console.log}
      style={{ maxWidth: '100%' }}
      id={id}
    >
      <Row>
        <Typography.Title level={3}>Datos principales</Typography.Title>
      </Row>
      <Row gutter={32}>
        <Col span={8}>
          <WhiteDisabledInput>
            <Form.Item<CardAffinityGroupFormInputs> name="id" label={'ID'}>
              <Input disabled />
            </Form.Item>
          </WhiteDisabledInput>
          <Form.Item<CardAffinityGroupFormInputs> name="endDate" label={'Fecha hasta'} rules={[{ required: true }]}>
            <DatePicker style={{ minWidth: '100%', maxWidth: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<CardAffinityGroupFormInputs> name="name" label={'Nombre'} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item<CardAffinityGroupFormInputs> name="active">
            <SwitchContainer>
              <p>Activo</p>
              <Switch value={active} defaultChecked onChange={() => setActive(!active)} />
            </SwitchContainer>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<CardAffinityGroupFormInputs> name="startDate" label={'Fecha desde'} rules={[{ required: true }]}>
            <DatePicker style={{ minWidth: '100%', maxWidth: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24} style={{ textAlign: 'right' }} >
          <Button type="primary" htmlType="submit" loading={loading} disabled={!submittable}>
            Guardar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const CardAffinityGroup = () => {
  const { cardAffinityGroupId } = useParams<{ cardAffinityGroupId: string }>();
  const { cardAffinityGroup } = useGetCardAffinityGroup(cardAffinityGroupId);
  const [fees, setFees] = useState<FeesFormInputs[]>([]);
  const [charges, setCharges] = useState<ChargesFormInputs[]>([]);
  const [emboss, setEmboss] = useState<EmbossFormInputs>();
  const [limit, setLimit] = useState<LimitsFormInputs>();
  const [usageLimit, setUsageLimit] = useState<UsageLimits>();

  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;

    if (hash) {
      const element = document.getElementById(hash.replace("#", ""));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  useEffect(() => {
    setFees(cardAffinityGroup?.fees);
    // @ts-ignore
    setCharges(cardAffinityGroup?.charges);
    setEmboss(cardAffinityGroup?.emboss);
    // @ts-ignore
    setLimit(cardAffinityGroup?.limits);
    // @ts-ignore
    setUsageLimit(cardAffinityGroup?.usage_limits)
  }, [cardAffinityGroup]);

  return (
    <Page title={'Grupo de afinidad'}>
      <AffinityGroup cardAffinityGroup={cardAffinityGroup} id={"main-data"} />
      <Emboss
        emboss={emboss}
        setEmboss={setEmboss}
        action={'edit'}
        cardAffinityGroupId={cardAffinityGroupId}
      />
      <div style={{ marginTop: '40px' }} id={"fees"}>
        <Fees
          fees={fees}
          setFees={setFees}
          cardAffinityGroupId={cardAffinityGroupId}
        />
      </div>
      <div style={{ marginTop: '40px' }} id={"charges"}>
        <Charges
          charges={charges}
          setCharges={(charges: ChargesFormInputs[]) => setCharges(charges)}
          cardAffinityGroupId={cardAffinityGroupId}
        />
      </div>
      <div style={{ marginTop: '40px' }} id={"limits"}>
        <Limits
          limit={limit}
          setLimit={setLimit}
          cardAffinityGroupId={cardAffinityGroupId}
          action={'edit'}
        />
      </div>
      <div style={{ marginTop: '10px' }} id={"usage-limits"}>
        <Typography.Title level={3}>Limitador de uso</Typography.Title>
        <UsageLimit
          usageLimit={usageLimit}
          setUsageLimit={setUsageLimit}
          action={'edit'}
          cardAffinityGroupId={cardAffinityGroupId}
        />
      </div>
    </Page>
  );
};

export default CardAffinityGroup;
